import { Injectable, effect, signal } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';

import { AppAuthService } from '@services/core/auth/app-auth.service';
import { OrganisationsApiService } from '@app/areas/organisations/services/organisations-api.service';
import { WorkspaceInvitationsApiService } from '@app/areas/organisations/services/workspace-invitations-api.service';
import { SessionBootstrapService } from '@app/shared/services/core/session-bootstrap/session-bootstrap.service';
import { WorkspaceSwitcherService } from '@app/shared/services/core/workspace-switcher/workspace-switcher.service';

export interface WorkspaceInviteAcceptanceContext {
  workspaceId: string;
  legalEntityId: string;
  workspaceName: string | null;
}

const STORAGE_KEY = 'fitmed_workspace_invite_token';

type InviteRedemptionState = 'idle' | 'waiting-auth' | 'redeeming' | 'accepted' | 'expired' | 'invalid' | 'redeemed' | 'error';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceInviteRedemptionService {
  readonly token$ = signal<string | null>(this.readTokenFromStorage());
  readonly state$ = signal<InviteRedemptionState>(this.token$() ? 'waiting-auth' : 'idle');
  readonly error$ = signal<string | null>(null);
  readonly acceptedInvitation$ = signal<WorkspaceInviteAcceptanceContext | null>(null);

  private inFlightToken: string | null = null;
  private settledToken: string | null = null;

  constructor(
    private readonly auth: AppAuthService,
    private readonly sessionBootstrap: SessionBootstrapService,
    private readonly invitesApi: WorkspaceInvitationsApiService,
    private readonly organisationsApi: OrganisationsApiService,
    private readonly workspaceSwitcher: WorkspaceSwitcherService,
  ) {
    effect(() => {
      const token = this.token$();
      const userId = this.auth.user$()?.identityProfile?.id ?? null;
      const ready = this.sessionBootstrap.ready$();

      if (!token) {
        return;
      }

      if (!userId || !ready) {
        this.state$.set('waiting-auth');
        return;
      }

      if (this.inFlightToken === token || this.settledToken === token) {
        return;
      }

      this.redeem(token);
    }, { allowSignalWrites: true });
  }

  registerToken(token: string | null | undefined): void {
    const normalized = `${token ?? ''}`.trim();
    if (!normalized) {
      this.clearToken(false);
      this.state$.set('invalid');
      this.error$.set('Missing workspace invitation token.');
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, normalized);
    this.acceptedInvitation$.set(null);
    this.error$.set(null);
    this.state$.set(this.auth.user$()?.identityProfile?.id && this.sessionBootstrap.ready$() ? 'redeeming' : 'waiting-auth');
    this.token$.set(normalized);
  }

  clearToken(resetState = true): void {
    sessionStorage.removeItem(STORAGE_KEY);
    this.token$.set(null);
    this.inFlightToken = null;
    if (resetState) {
      this.acceptedInvitation$.set(null);
      this.error$.set(null);
      this.state$.set('idle');
    }
  }

  clearAcceptedInvitation(): void {
    this.acceptedInvitation$.set(null);
  }

  private redeem(token: string): void {
    this.inFlightToken = token;
    this.state$.set('redeeming');
    this.error$.set(null);

    this.invitesApi.redeemInvitation(token)
      .pipe(take(1))
      .subscribe({
        next: invitation => {
          this.settledToken = token;
          this.clearToken(false);
          this.inFlightToken = null;

          void this.handleAcceptedInvitation(invitation)
            .then(acceptedInvitation => {
              this.acceptedInvitation$.set(acceptedInvitation);
              this.state$.set('accepted');
            })
            .catch(err => {
              console.error('[WorkspaceInviteRedemption] accepted invitation bootstrap failed', err);
              this.acceptedInvitation$.set({
                workspaceId: invitation.workspaceId,
                legalEntityId: invitation.legalEntityId,
                workspaceName: null,
              });
              this.state$.set('accepted');
            });
        },
        error: err => {
          this.inFlightToken = null;
          const reason = this.classifyFailure(err);
          if (reason === 'expired' || reason === 'invalid' || reason === 'redeemed') {
            this.settledToken = token;
            this.clearToken(false);
            this.state$.set(reason);
            this.error$.set(this.failureMessage(reason));
            return;
          }

          this.state$.set('error');
          this.error$.set(this.failureMessage(reason));
        },
    });
  }

  private async handleAcceptedInvitation(invitation: {
    workspaceId: string;
    legalEntityId: string;
  }): Promise<WorkspaceInviteAcceptanceContext> {
    const workspaceName = await this.seedSelectionFromInvitation(invitation);
    this.workspaceSwitcher.load(undefined, undefined, true);
    return {
      workspaceId: invitation.workspaceId,
      legalEntityId: invitation.legalEntityId,
      workspaceName,
    };
  }

  private async seedSelectionFromInvitation(invitation: {
    workspaceId: string;
    legalEntityId: string;
  }): Promise<string | null> {
    const legalEntityId = `${invitation?.legalEntityId ?? ''}`.trim();
    const workspaceId = `${invitation?.workspaceId ?? ''}`.trim();

    if (!legalEntityId || !workspaceId) {
      return null;
    }

    try {
      const [legalEntity, workspaces] = await Promise.all([
        firstValueFrom(this.organisationsApi.getLegalEntity(legalEntityId)),
        firstValueFrom(this.organisationsApi.listWorkspaces(legalEntityId)),
      ]);

      const workspace = workspaces.find(item => item.id === workspaceId) ?? null;
      if (!workspace) {
        return null;
      }

      this.workspaceSwitcher.syncSelection(
        legalEntity as any,
        workspace as any,
        { applyRegion: true },
      );

      return `${workspace.name ?? ''}`.trim() || null;
    } catch (err) {
      console.debug('[WorkspaceInviteRedemption] failed to seed workspace selection from invitation', {
        invitationId: invitation?.workspaceId ?? null,
        legalEntityId,
        workspaceId,
        err,
      });

      return null;
    }
  }

  private classifyFailure(err: any): Exclude<InviteRedemptionState, 'idle' | 'waiting-auth' | 'redeeming' | 'accepted'> {
    const raw = `${err?.error?.message ?? err?.message ?? err ?? ''}`.toLowerCase();

    if (raw.includes('expired')) {
      return 'expired';
    }

    if (raw.includes('already_redeemed') || raw.includes('already redeemed') || raw.includes('redeemed')) {
      return 'redeemed';
    }

    if (raw.includes('redeemed') || raw.includes('invalid') || raw.includes('not_found')) {
      return 'invalid';
    }

    return 'error';
  }

  private failureMessage(state: 'expired' | 'invalid' | 'redeemed' | 'error'): string {
    switch (state) {
      case 'expired':
        return 'This workspace invitation has expired and can no longer be used.';
      case 'redeemed':
        return 'This workspace invitation has already been accepted and is no longer active.';
      case 'invalid':
        return 'This workspace invitation is no longer valid and may have already been claimed or expired.';
      default:
        return 'We could not redeem the workspace invitation right now. It may have already been claimed or expired.';
    }
  }

  private readTokenFromStorage(): string | null {
    const token = (sessionStorage.getItem(STORAGE_KEY) ?? '').trim();
    return token || null;
  }
}
